import { useState } from "react";
import { TrendingUp, BookOpen, Monitor, ArrowRight, Layers, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList, ReferenceLine } from "recharts";
import { usePrefetchOrFetch } from "../common/DataPrefetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SIGNAL_CONFIG = {
  untapped: { color: "#f59e0b", label: "Untapped Potential", desc: "High research intensity, few commercial products" },
  "over-productized": { color: "#ef4444", label: "Over-productized", desc: "Many products, relatively little research backing" },
  balanced: { color: "#10b981", label: "Balanced", desc: "Healthy research-to-product ratio" },
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-[#0c1222] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl max-w-60">
      <p className="font-bold text-white mb-1">{d.industry}</p>
      <p className="text-cyan-400">Research papers: {d.research_papers?.toLocaleString()}</p>
      <p className="text-emerald-400">Products: {d.products?.toLocaleString()}</p>
      <p className="text-slate-400">Ratio: {d.ratio}x</p>
      <p className="text-slate-400">Research care %: {d.research_care_pct}%</p>
      <p className="text-slate-400">Product care %: {d.product_care_pct}%</p>
    </div>
  );
};

export const ResearchProductGap = ({ compact = false }) => {
  const [data, loading] = usePrefetchOrFetch(
    "researchIndustryGap",
    `${API}/research/industry-gap`,
    {
      validate: (d) => d && Array.isArray(d.gaps) && d.gaps.length > 0,
      delay: 500,
    }
  );
  const [expanded, setExpanded] = useState(false);
  const [sortBy, setSortBy] = useState("ratio");

  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-4 w-4 bg-white/[0.04] animate-pulse rounded" />
          <div className="h-3 w-36 bg-white/[0.04] animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[1,2,3].map(i => <div key={i} className="text-center space-y-2"><div className="h-8 w-16 mx-auto bg-white/[0.04] animate-pulse rounded" /><div className="h-2 w-20 mx-auto bg-white/[0.04] animate-pulse rounded" /></div>)}
        </div>
        <div className="h-48 bg-white/[0.04] animate-pulse rounded" />
      </div>
    );
  }

  if (!data?.gaps?.length) return null;

  const gaps = data.gaps;
  const untapped = gaps.filter(g => g.signal === "untapped");
  const overProd = gaps.filter(g => g.signal === "over-productized");
  const avgRatio = (gaps.reduce((s, g) => s + g.ratio, 0) / gaps.length).toFixed(1);

  // Chart data - top 8 by ratio with short labels
  const INDUSTRY_SHORT = {
    "Healthcare & Life Sciences": "Healthcare",
    "Social Impact & Nonprofit": "Social Impact",
    "Social Impact & Nonprofits": "Social Impact",
    "Manufacturing & Industrial": "Manufacturing",
    "Education & Research": "Education",
    "Agriculture & Environment": "Agriculture",
    "Finance & Insurance": "Finance",
    "Transportation & Logistics": "Transport",
    "Security & Defense": "Security",
    "Developer Tools & Infrastructure": "Dev Tools",
    "Entertainment & Gaming": "Entertainment",
    "Creative & Media": "Creative",
    "Retail & E-commerce": "Retail",
    "Legal & Compliance": "Legal",
    "General Productivity": "Productivity",
    "Communication & Telecom": "Telecom",
  };
  const chartData = (() => {
    const base = gaps.map(g => ({
      ...g,
      name: g.industry.replace(/\s*&\s*/g, " & ").replace(/ \/ .*/, ""),
      shortName: INDUSTRY_SHORT[g.industry] || (g.industry?.length > 12 ? g.industry.substring(0, 10) + ".." : g.industry),
    }));
    const sortKey = { ratio: "ratio", papers: "research_papers", products: "products", care: "care_gap" }[sortBy] || "ratio";
    return [...base].sort((a, b) => Math.abs(b[sortKey] || 0) - Math.abs(a[sortKey] || 0));
  })();

  if (compact) {
    // Compact version for consciousness dashboard
    return (
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="research-product-gap">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-4 h-4 text-cyan-400" />
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Research-Product Gap</h3>
          {/* Sort-by segmented control */}
          <div className="ml-auto inline-flex items-center gap-0.5 p-0.5 rounded-lg bg-white/[0.03] border border-white/[0.06]" data-testid="rpg-sort-control">
            {[
              { id: "ratio", label: "Ratio" },
              { id: "papers", label: "Papers" },
              { id: "products", label: "Products" },
              { id: "care", label: "Care gap" },
            ].map(opt => (
              <button key={opt.id} onClick={() => setSortBy(opt.id)} data-testid={`rpg-sort-${opt.id}`}
                className={`px-2.5 py-1 text-[10px] font-bold rounded-md uppercase tracking-wider transition-all ${
                  sortBy === opt.id
                    ? "bg-cyan-500/20 text-cyan-200"
                    : "text-slate-500 hover:text-slate-300"
                }`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center">
            <p className="text-2xl font-black text-cyan-400">{avgRatio}x</p>
            <p className="text-[9px] text-slate-500">Avg Research/Product Ratio</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-black text-amber-400">{untapped.length}</p>
            <p className="text-[9px] text-slate-500">Untapped Domains</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-black text-red-400">{overProd.length}</p>
            <p className="text-[9px] text-slate-500">Over-productized</p>
          </div>
        </div>

        {/* Bar Chart */}
        <div className="mb-4" style={{ minHeight: 280, height: expanded ? Math.max(28 * chartData.length + 60, 280) : 280 }} data-testid="rpg-chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={expanded ? chartData : chartData.slice(0, 8)} layout="vertical" margin={{ left: 0, right: 50, top: 5, bottom: 5 }}>
              <defs>
                <linearGradient id="rpg-untapped" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={1} />
                </linearGradient>
                <linearGradient id="rpg-over" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#fb7185" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={1} />
                </linearGradient>
                <linearGradient id="rpg-balanced" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#34d399" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={1} />
                </linearGradient>
              </defs>
              <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 10 }} stroke="#334155" />
              <YAxis type="category" dataKey="shortName" width={110} tick={{ fill: "#e2e8f0", fontSize: 11, fontWeight: 600 }} interval={0} stroke="#334155" />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <ReferenceLine
                x={Number(avgRatio)}
                stroke="#06b6d4"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{ value: `avg ${avgRatio}x`, position: "top", fill: "#22d3ee", fontSize: 10, fontWeight: 700 }}
              />
              <Bar dataKey="ratio" name="R/P Ratio" radius={[0, 6, 6, 0]} animationDuration={900}>
                {(expanded ? chartData : chartData.slice(0, 8)).map((g) => {
                  const grad = g.signal === "over-productized" ? "url(#rpg-over)" : g.signal === "balanced" ? "url(#rpg-balanced)" : "url(#rpg-untapped)";
                  return <Cell key={g.industry} fill={grad} />;
                })}
                <LabelList dataKey="ratio" position="right" formatter={(v) => `${v}x`} style={{ fill: "#fbbf24", fontSize: 11, fontWeight: 700 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {chartData.length > 8 && (
          <div className="flex justify-center -mt-2 mb-3">
            <button
              onClick={() => setExpanded(v => !v)}
              data-testid="rpg-expand-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-[10px] font-bold uppercase tracking-wider transition-all">
              {expanded ? <><ChevronUp className="w-3 h-3" /> Show top 8</> : <><ChevronDown className="w-3 h-3" /> Show all {chartData.length} industries</>}
            </button>
          </div>
        )}

        {/* Signal Legend */}
        <div className="flex flex-wrap gap-3 pt-2 border-t border-white/[0.04]">
          {Object.entries(SIGNAL_CONFIG).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background: v.color }} />
              <span className="text-[9px] text-slate-400">{v.label}</span>
            </div>
          ))}
        </div>

        {/* Top untapped domains */}
        {untapped.length > 0 && (
          <div className="mt-3 pt-3 border-t border-white/[0.04]">
            <p className="text-[9px] font-bold text-amber-400/60 uppercase tracking-wider mb-2">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              Biggest Innovation Gaps
            </p>
            <div className="space-y-1.5">
              {untapped.slice(0, 3).map(g => (
                <div key={g.industry} className="flex items-center justify-between">
                  <span className="text-[10px] text-white">{g.industry}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-cyan-400">{g.research_papers?.toLocaleString()} papers</span>
                    <ArrowRight className="w-3 h-3 text-slate-600" />
                    <span className="text-[9px] text-emerald-400">{g.products?.toLocaleString()} products</span>
                    <span className="text-[9px] font-bold text-amber-400">{g.ratio}x</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full version
  return (
    <div className="space-y-6" data-testid="research-product-gap-full">
      {/* Hero */}
      <div className="relative rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #0a1520 50%, #0f0a1a 100%)", border: "1px solid rgba(6,182,212,0.12)" }}>
        <div className="px-8 py-8">
          <h2 className="text-2xl font-black text-white">Research-Product Gap Analysis</h2>
          <p className="text-sm text-slate-400 mt-1">Measuring the delta between academic AI research intensity and commercial product development across {gaps.length} industries</p>
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div>
              <p className="text-3xl font-black text-cyan-400">{avgRatio}x</p>
              <p className="text-[10px] text-slate-500">Average Research-to-Product Ratio</p>
            </div>
            <div>
              <p className="text-3xl font-black text-amber-400">{untapped.length}</p>
              <p className="text-[10px] text-slate-500">Untapped Innovation Domains</p>
            </div>
            <div>
              <p className="text-3xl font-black text-red-400">{overProd.length}</p>
              <p className="text-[10px] text-slate-500">Over-productized Domains</p>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Research vs Product Intensity</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
              <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
              <YAxis type="category" dataKey="shortName" width={110} tick={{ fill: "#94a3b8", fontSize: 10 }} interval={0} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="ratio" name="R/P Ratio" radius={[0, 6, 6, 0]}>
                {chartData.map((g) => (
                  <Cell key={g.industry} fill={SIGNAL_CONFIG[g.signal]?.color || "#64748b"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Industry cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {gaps.map(g => {
          const cfg = SIGNAL_CONFIG[g.signal] || SIGNAL_CONFIG.balanced;
          return (
            <div key={g.industry} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 hover:bg-white/[0.03] transition-colors">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold text-white">{g.industry}</h4>
                <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ color: cfg.color, background: `${cfg.color}15`, border: `1px solid ${cfg.color}25` }}>
                  {cfg.label}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div>
                  <p className="text-lg font-black text-cyan-400">{g.research_papers?.toLocaleString()}</p>
                  <p className="text-[8px] text-slate-500 flex items-center gap-1"><BookOpen className="w-3 h-3" />Papers</p>
                </div>
                <div>
                  <p className="text-lg font-black text-emerald-400">{g.products?.toLocaleString()}</p>
                  <p className="text-[8px] text-slate-500 flex items-center gap-1"><Monitor className="w-3 h-3" />Products</p>
                </div>
                <div>
                  <p className="text-lg font-black" style={{ color: cfg.color }}>{g.ratio}x</p>
                  <p className="text-[8px] text-slate-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Ratio</p>
                </div>
              </div>
              {g.care_gap !== 0 && (
                <div className="mt-2 pt-2 border-t border-white/[0.04]">
                  <p className="text-[9px] text-slate-400">
                    Care gap: <span className={g.care_gap > 0 ? "text-emerald-400" : "text-red-400"}>{g.care_gap > 0 ? "+" : ""}{g.care_gap}%</span>
                    <span className="text-slate-600 ml-1">(research vs products)</span>
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
